import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { User, Lock, Mail, Eye, EyeOff } from "lucide-react";
import { useGoogleLogin } from "@react-oauth/google";

import API from "../api/api";
import { setUser, setCredits } from "../store/userSlice";
import { clearActiveChat } from "../store/chatSlice";
import { DeptSelector } from "../components/Popups";
import { useLanguage, getTranslation } from "../utils/language";
import { GRGPT_GOLD_LOGO } from "../assets/logoBase64";
import "../style/login.css";

// Form Stagger Motion Variants
const formContainerVariants = {
  hidden: { opacity: 0, y: 8 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      staggerChildren: 0.06,
      duration: 0.25,
    },
  },
  exit: {
    opacity: 0,
    y: -8,
    transition: { duration: 0.12 },
  },
};

const formItemVariants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.2 } },
};

// Crisp Google SVG Icon
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
        fill="#EA4335"
      />
    </svg>
  );
}

export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [language] = useLanguage();

  const [tab, setTab] = useState("signin"); // "signin" | "signup" | "verify"
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [postAuthUser, setPostAuthUser] = useState(null);
  const [showDeptSelector, setShowDeptSelector] = useState(false);

  // Email OTP Verification States
  const [verificationEmail, setVerificationEmail] = useState("");
  const [verificationOtp, setVerificationOtp] = useState(["", "", "", "", "", ""]);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [devOtpHint, setDevOtpHint] = useState("");
  const otpInputRefs = useRef([]);

  // Forgot & Reset Password States
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetOtp, setResetOtp] = useState(["", "", "", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetCooldown, setResetCooldown] = useState(0);
  const [resetDevOtpHint, setResetDevOtpHint] = useState("");
  const resetOtpInputRefs = useRef([]);

  // Form States
  const [signInForm, setSignInForm] = useState({ email: "", password: "" });
  const [signUpForm, setSignUpForm] = useState({ name: "", email: "", password: "" });

  // If navigated here with emailVerificationRequired (e.g. from signup page or modal)
  useEffect(() => {
    if (location.state?.emailVerificationRequired && location.state?.email) {
      setVerificationEmail(location.state.email);
      setTab("verify");
      setVerificationOtp(["", "", "", "", "", ""]);
      if (location.state.devOtp) {
        setDevOtpHint(location.state.devOtp);
      }
      setInfoMessage("Verification code sent to your email. Please enter it below.");
      setResendCooldown(60);
    }
  }, [location.state]);

  // Cooldown countdown timer for OTP resend (Email verification & Password reset)
  useEffect(() => {
    if (resendCooldown <= 0 && resetCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
      setResetCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown, resetCooldown]);

  const switchTab = (t) => {
    setTab(t);
    setError("");
    setInfoMessage("");
    setShowPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    if (t !== "verify") {
      setDevOtpHint("");
    }
    if (t !== "reset") {
      setResetDevOtpHint("");
    }
    if (t === "forgot" && !forgotEmail && signInForm.email) {
      setForgotEmail(signInForm.email);
    }
  };

  const finishAuth = ({ token, user }) => {
    localStorage.setItem("grgpt_token", token);
    localStorage.setItem("token", token);

    try {
      let stored = JSON.parse(localStorage.getItem("grgpt_saved_accounts") || "[]");
      const currentAccData = {
        id: user._id || "current-user",
        name: user.name || "User",
        email: user.email || "",
        avatar: user.profilePicture || user.avatar || "",
        token: token,
        userObj: user
      };
      const existingIndex = stored.findIndex(
        (acc) => (acc.email && user.email && acc.email.toLowerCase() === user.email.toLowerCase()) || (acc.id && acc.id === user._id)
      );
      if (existingIndex >= 0) {
        stored[existingIndex] = { ...stored[existingIndex], ...currentAccData };
      } else {
        stored.unshift(currentAccData);
      }
      localStorage.setItem("grgpt_saved_accounts", JSON.stringify(stored));
    } catch (e) {
      console.error("Error saving account to grgpt_saved_accounts:", e);
    }

    dispatch(setUser(user));
    dispatch(setCredits(user?.credits ?? 10));
    dispatch(clearActiveChat());

    const hasDepartments = Array.isArray(user?.departments) && user.departments.length > 0;
    if (hasDepartments) {
      navigate("/", { replace: true });
      return;
    }

    setPostAuthUser(user);
    setShowDeptSelector(true);
  };

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setLoading(true);
        setError("");
        const res = await API.post("/auth/google", {
          accessToken: tokenResponse.access_token,
        });
        finishAuth(res.data);
      } catch (err) {
        console.error("Google sign-in error:", err);
        setError(
          err.response?.data?.message || "Google sign-in failed. Please try again."
        );
      } finally {
        setLoading(false);
      }
    },
    onError: (err) => {
      console.error("Google sign-in error:", err);
      setError("Google sign-in was cancelled or encountered an error.");
    },
  });

  const handleDeptDone = async (selectedIds, selectedDepts = []) => {
    if (selectedDepts.length > 0) {
      localStorage.setItem(
        "grgpt_preferred_dept_names",
        JSON.stringify(selectedDepts.map((d) => d.name))
      );
    }

    setShowDeptSelector(false);

    if (selectedIds?.length > 0) {
      try {
        await API.post("/user/preferences", { departments: selectedIds });
      } catch (err) {
        console.error("Dept save failed", err);
      }
    }

    navigate("/", { replace: true });
  };

  const handleDeptClose = () => {
    setShowDeptSelector(false);
    navigate("/", { replace: true });
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");

    const email = signInForm.email.trim();
    setSignInForm((prev) => ({ ...prev, email }));
    if (!email || email.length > 254) {
      setError("Please enter a valid email address.");
      return;
    }
    const emailRegex = /^[^\s@]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      setLoading(true);
      const res = await API.post("/auth/login", {
        ...signInForm,
        email: email
      });
      finishAuth(res.data);
    } catch (err) {
      const respData = err.response?.data;
      if (respData?.emailVerificationRequired) {
        const targetEmail = respData.email || email;
        setVerificationEmail(targetEmail);
        setDevOtpHint(respData.devOtp || "");
        setTab("verify");
        setVerificationOtp(["", "", "", "", "", ""]);
        setInfoMessage("Please enter the 6-digit verification code sent to your email.");
        setResendCooldown(60);
      } else {
        setError(respData?.message || "Login failed. Please check your credentials.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");

    const name = signUpForm.name.trim();
    if (!name) {
      setError("Full Name is required");
      return;
    }
    if (name.length > 100) {
      setError("Full Name cannot exceed 100 characters");
      return;
    }
    if (/[<>{}[\];=*#%@!$]/.test(name)) {
      setError("Full Name contains invalid special characters");
      return;
    }

    const email = signUpForm.email.trim();
    setSignUpForm((prev) => ({ ...prev, email }));
    if (!email || email.length > 254) {
      setError("Please enter a valid email address.");
      return;
    }
    const emailRegex = /^[^\s@]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    const password = signUpForm.password;
    if (!password || password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }
    if (password.length > 128) {
      setError("Password cannot exceed 128 characters");
      return;
    }

    const pwdMissing = [];
    if (!/[A-Z]/.test(password)) pwdMissing.push("uppercase letter");
    if (!/[a-z]/.test(password)) pwdMissing.push("lowercase letter");
    if (!/[0-9]/.test(password)) pwdMissing.push("number");
    if (!/[^A-Za-z0-9]/.test(password)) pwdMissing.push("special character");

    if (pwdMissing.length > 0) {
      if (pwdMissing.length === 1) {
        setError(`Password must include at least one ${pwdMissing[0]}.`);
      } else {
        const last = pwdMissing.pop();
        setError(`Password must include at least one ${pwdMissing.join(", ")} and ${last}.`);
      }
      return;
    }

    try {
      setLoading(true);
      const res = await API.post("/auth/signup", {
        name,
        email,
        password
      });

      if (res.data?.emailVerificationRequired) {
        setVerificationEmail(email);
        setDevOtpHint(res.data.devOtp || "");
        setTab("verify");
        setVerificationOtp(["", "", "", "", "", ""]);
        setInfoMessage("Account created! Please enter the 6-digit verification code sent to your email.");
        setResendCooldown(60);
      } else {
        finishAuth(res.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── OTP Digit Input Handlers ──
  const handleOtpDigitChange = (index, value) => {
    const cleaned = value.replace(/\D/g, "");
    const nextOtp = [...verificationOtp];

    if (!cleaned) {
      nextOtp[index] = "";
      setVerificationOtp(nextOtp);
      return;
    }

    const char = cleaned.slice(-1);
    nextOtp[index] = char;
    setVerificationOtp(nextOtp);

    if (index < 5 && otpInputRefs.current[index + 1]) {
      otpInputRefs.current[index + 1].focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !verificationOtp[index] && index > 0) {
      if (otpInputRefs.current[index - 1]) {
        otpInputRefs.current[index - 1].focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").trim().replace(/\D/g, "");
    if (!pasteData) return;
    const digits = pasteData.slice(0, 6).split("");
    const nextOtp = [...verificationOtp];
    for (let i = 0; i < 6; i++) {
      nextOtp[i] = digits[i] || "";
    }
    setVerificationOtp(nextOtp);
    const lastFilled = Math.min(digits.length, 5);
    otpInputRefs.current[lastFilled]?.focus();
  };

  const handleVerifyOtp = async (e) => {
    if (e) e.preventDefault();
    if (loading) return;
    const code = verificationOtp.join("").trim();
    if (code.length !== 6) {
      setError("Please enter the complete 6-digit verification code.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const res = await API.post("/auth/verify-email-otp", {
        email: verificationEmail,
        otp: code
      });

      if (res.data?.token) {
        finishAuth(res.data);
      } else {
        setError("Verification succeeded, please sign in.");
        setTab("signin");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Invalid or expired verification code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0 || loading) return;
    try {
      setLoading(true);
      setError("");
      const res = await API.post("/auth/resend-email-otp", {
        email: verificationEmail
      });
      setInfoMessage(res.data?.message || "A new verification code has been sent to your email.");
      if (res.data?.otp) {
        setDevOtpHint(res.data.otp);
      }
      setResendCooldown(res.data?.remainingSeconds || 60);
    } catch (err) {
      if (err.response?.data?.remainingSeconds) {
        setResendCooldown(err.response.data.remainingSeconds);
      }
      setError(err.response?.data?.message || "Failed to resend verification code. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Reset OTP Digits handling
  const handleResetOtpDigitChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const nextOtp = [...resetOtp];
    nextOtp[index] = digit;
    setResetOtp(nextOtp);

    if (digit && index < 5) {
      resetOtpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleResetOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !resetOtp[index] && index > 0) {
      resetOtpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleResetOtpPaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").trim().replace(/\D/g, "");
    if (!pasteData) return;
    const digits = pasteData.slice(0, 6).split("");
    const nextOtp = [...resetOtp];
    for (let i = 0; i < 6; i++) {
      nextOtp[i] = digits[i] || "";
    }
    setResetOtp(nextOtp);
    const lastFilled = Math.min(digits.length, 5);
    resetOtpInputRefs.current[lastFilled]?.focus();
  };

  const handleForgotPasswordRequest = async (e) => {
    if (e) e.preventDefault();
    if (loading) return;

    const emailToUse = (forgotEmail || signInForm.email || "").trim().toLowerCase();
    if (!emailToUse) {
      setError("Please enter your registered email address.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setInfoMessage("");
      const res = await API.post("/auth/forgot-password", { email: emailToUse });

      setForgotEmail(emailToUse);
      setResetOtp(["", "", "", "", "", ""]);
      setNewPassword("");
      setConfirmPassword("");
      if (res.data?.devOtp) {
        setResetDevOtpHint(res.data.devOtp);
      } else {
        setResetDevOtpHint("");
      }
      setResetCooldown(60);
      setInfoMessage(res.data?.message || "Password reset code sent to your email.");
      setTab("reset");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send reset code. Please check your email and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendResetOtp = async () => {
    if (resetCooldown > 0 || loading) return;
    try {
      setLoading(true);
      setError("");
      const res = await API.post("/auth/forgot-password", { email: forgotEmail });
      setInfoMessage(res.data?.message || "A new password reset code has been sent to your email.");
      if (res.data?.devOtp) {
        setResetDevOtpHint(res.data.devOtp);
      }
      setResetCooldown(res.data?.remainingSeconds || 60);
    } catch (err) {
      if (err.response?.data?.remainingSeconds) {
        setResetCooldown(err.response.data.remainingSeconds);
      }
      setError(err.response?.data?.message || "Failed to resend reset code. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    if (e) e.preventDefault();
    if (loading) return;

    const code = resetOtp.join("").trim();
    if (code.length !== 6) {
      setError("Please enter the complete 6-digit reset code.");
      return;
    }

    if (!newPassword) {
      setError("Please enter a new password.");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    const pwdMissing = [];
    if (!/[A-Z]/.test(newPassword)) pwdMissing.push("uppercase letter");
    if (!/[a-z]/.test(newPassword)) pwdMissing.push("lowercase letter");
    if (!/[0-9]/.test(newPassword)) pwdMissing.push("number");
    if (!/[^A-Za-z0-9]/.test(newPassword)) pwdMissing.push("special character");

    if (pwdMissing.length > 0) {
      let msg = "";
      if (pwdMissing.length === 1) {
        msg = `Password must include at least one ${pwdMissing[0]}.`;
      } else {
        const last = pwdMissing.pop();
        msg = `Password must include at least one ${pwdMissing.join(", ")} and ${last}.`;
      }
      setError(msg);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match. Please re-enter.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const res = await API.post("/auth/reset-password", {
        email: forgotEmail,
        otp: code,
        newPassword
      });

      setInfoMessage(res.data?.message || "Password reset successfully! Please sign in with your new password.");
      setSignInForm((prev) => ({ ...prev, email: forgotEmail, password: "" }));
      setTab("signin");
      setResetOtp(["", "", "", "", "", ""]);
      setNewPassword("");
      setConfirmPassword("");
      setResetDevOtpHint("");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to reset password. Please check the code and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page-wrapper">
      {/* ── Main Split-Screen Portal ── */}
      <motion.div
        className="auth-portal-container"
        initial={{ opacity: 0, scale: 0.98, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 140, damping: 20 }}
      >
        {/* ── Left Visual Panel (Layered Gold Banners + Connected Nav Tabs) ── */}
        <div className="auth-visual-panel">
          {/* Overlapping Gold Diagonal Ribbon Layers */}
          <div className="banner-ribbon-1" />
          <div className="banner-ribbon-2" />
          <div className="banner-ribbon-3" />
          <div className="banner-ribbon-4" />

          {/* Top Brand Watermark Monogram */}
          <div className="banner-brand-watermark">
            <img
              src={GRGPT_GOLD_LOGO}
              alt="GRGPT"
              className="banner-brand-logo"
            />
            <span>GRGPT</span>
          </div>

          {/* Connected Vertical Tab Switcher */}
          <div className="auth-left-nav">
            <div className="nav-tab-item">
              {tab === "signin" ? (
                <motion.div
                  className="nav-tab-active-pill"
                  layoutId="activeTabNotch"
                  transition={{ type: "spring", stiffness: 380, damping: 28 }}
                >
                  LOGIN
                </motion.div>
              ) : (
                <button
                  type="button"
                  className="nav-tab-btn-inactive"
                  onClick={() => switchTab("signin")}
                >
                  LOGIN
                </button>
              )}
            </div>

            <div className="nav-tab-item">
              {tab === "signup" ? (
                <motion.div
                  className="nav-tab-active-pill"
                  layoutId="activeTabNotch"
                  transition={{ type: "spring", stiffness: 380, damping: 28 }}
                >
                  SIGN UP
                </motion.div>
              ) : (
                <button
                  type="button"
                  className="nav-tab-btn-inactive"
                  onClick={() => switchTab("signup")}
                >
                  SIGN UP
                </button>
              )}
            </div>

            {tab === "verify" && (
              <div className="nav-tab-item">
                <motion.div
                  className="nav-tab-active-pill"
                  layoutId="activeTabNotch"
                  transition={{ type: "spring", stiffness: 380, damping: 28 }}
                >
                  VERIFY
                </motion.div>
              </div>
            )}

            {(tab === "forgot" || tab === "reset") && (
              <div className="nav-tab-item">
                <motion.div
                  className="nav-tab-active-pill"
                  layoutId="activeTabNotch"
                  transition={{ type: "spring", stiffness: 380, damping: 28 }}
                >
                  RESET
                </motion.div>
              </div>
            )}
          </div>
        </div>

        {/* ── Right Form Panel ── */}
        <div className="auth-form-panel">
          <div>
            {/* Circular Profile Avatar */}
            <div className="auth-avatar-header">
              <div className="auth-profile-circle">
                {tab === "verify" || tab === "forgot" ? (
                  <Mail size={36} color="#ffffff" />
                ) : tab === "reset" ? (
                  <Lock size={36} color="#ffffff" />
                ) : (
                  <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="auth-profile-svg">
                    <circle cx="20" cy="15" r="7" fill="rgba(255,255,255,0.9)" />
                    <ellipse cx="20" cy="33" rx="12" ry="8" fill="rgba(255,255,255,0.9)" />
                  </svg>
                )}
              </div>
              <h2 className="auth-avatar-title">
                {tab === "signin"
                  ? "LOGIN"
                  : tab === "signup"
                    ? "SIGN UP"
                    : tab === "verify"
                      ? "VERIFY EMAIL"
                      : tab === "forgot"
                        ? "FORGOT PASSWORD"
                        : "RESET PASSWORD"}
              </h2>
            </div>

            {/* Error & Info Alerts */}
            {error && <div className="auth-alert-error">{error}</div>}
            {infoMessage && (
              <div
                className={
                  infoMessage.toLowerCase().includes("success")
                    ? "auth-alert-success"
                    : "auth-alert-info"
                }
              >
                {infoMessage}
              </div>
            )}

            {/* ── FORMS SWITCHER ── */}
            <AnimatePresence mode="wait">
              {tab === "signin" && (
                <motion.form
                  key="signin-form"
                  onSubmit={handleSignIn}
                  variants={formContainerVariants}
                  initial="hidden"
                  animate="show"
                  exit="exit"
                >
                  <div className="auth-form-grid">
                    <motion.div className="auth-line-field" variants={formItemVariants}>
                      <User className="auth-line-icon" size={18} />
                      <input
                        name="email"
                        type="email"
                        placeholder="Email"
                        value={signInForm.email}
                        onChange={(e) =>
                          setSignInForm({ ...signInForm, email: e.target.value })
                        }
                        required
                        autoComplete="username"
                        className="auth-line-input"
                      />
                    </motion.div>

                    <motion.div className="auth-line-field" variants={formItemVariants}>
                      <Lock className="auth-line-icon" size={18} />
                      <input
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder={getTranslation(language, "login", "password")}
                        value={signInForm.password}
                        onChange={(e) =>
                          setSignInForm({ ...signInForm, password: e.target.value })
                        }
                        required
                        autoComplete="current-password"
                        className="auth-line-input"
                      />
                      <button
                        type="button"
                        className="auth-password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </motion.div>
                  </div>

                  <motion.div className="auth-actions-row" variants={formItemVariants}>
                    <button
                      type="button"
                      className="auth-forgot-link"
                      onClick={() => {
                        setForgotEmail(signInForm.email || "");
                        switchTab("forgot");
                      }}
                    >
                      Forgot Password?
                    </button>

                    <motion.button
                      type="submit"
                      disabled={loading}
                      className="auth-btn-pill"
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      {loading ? "..." : "LOGIN"}
                    </motion.button>
                  </motion.div>
                </motion.form>
              )}

              {/* ── SIGN UP FORM ── */}
              {tab === "signup" && (
                <motion.form
                  key="signup-form"
                  onSubmit={handleSignUp}
                  variants={formContainerVariants}
                  initial="hidden"
                  animate="show"
                  exit="exit"
                >
                  <div className="auth-form-grid">
                    <motion.div className="auth-line-field" variants={formItemVariants}>
                      <User className="auth-line-icon" size={18} />
                      <input
                        name="name"
                        type="text"
                        placeholder="Full Name"
                        value={signUpForm.name}
                        onChange={(e) =>
                          setSignUpForm({ ...signUpForm, name: e.target.value })
                        }
                        required
                        autoComplete="name"
                        className="auth-line-input"
                      />
                    </motion.div>

                    <motion.div className="auth-line-field" variants={formItemVariants}>
                      <Mail className="auth-line-icon" size={18} />
                      <input
                        name="email"
                        type="email"
                        placeholder="Email Address"
                        value={signUpForm.email}
                        onChange={(e) =>
                          setSignUpForm({ ...signUpForm, email: e.target.value })
                        }
                        required
                        autoComplete="username"
                        className="auth-line-input"
                      />
                    </motion.div>

                    <motion.div className="auth-line-field" variants={formItemVariants}>
                      <Lock className="auth-line-icon" size={18} />
                      <input
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder={getTranslation(language, "login", "password")}
                        value={signUpForm.password}
                        onChange={(e) =>
                          setSignUpForm({ ...signUpForm, password: e.target.value })
                        }
                        required
                        autoComplete="new-password"
                        className="auth-line-input"
                      />
                      <button
                        type="button"
                        className="auth-password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </motion.div>
                  </div>

                  <motion.div className="auth-actions-row" variants={formItemVariants}>
                    <button
                      type="button"
                      className="auth-forgot-link"
                      onClick={() => switchTab("signin")}
                    >
                      Already have an account?
                    </button>

                    <motion.button
                      type="submit"
                      disabled={loading}
                      className="auth-btn-pill"
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      {loading ? "..." : "SIGN UP"}
                    </motion.button>
                  </motion.div>
                </motion.form>
              )}

              {/* ── EMAIL OTP VERIFICATION FORM ── */}
              {tab === "verify" && (
                <motion.form
                  key="verify-form"
                  onSubmit={handleVerifyOtp}
                  variants={formContainerVariants}
                  initial="hidden"
                  animate="show"
                  exit="exit"
                >
                  <div className="auth-verify-intro">
                    <p className="auth-verify-desc">
                      Enter the 6-digit verification code sent to
                    </p>
                    <div className="auth-verify-email-badge">
                      <Mail size={14} />
                      <span>{verificationEmail}</span>
                    </div>
                  </div>

                  {devOtpHint && (
                    <div className="auth-dev-badge">
                      Dev Mode Verification OTP: <strong>{devOtpHint}</strong>
                    </div>
                  )}

                  {/* 6-box OTP Input */}
                  <div className="auth-otp-row" onPaste={handleOtpPaste}>
                    {verificationOtp.map((digit, idx) => (
                      <input
                        key={idx}
                        ref={(el) => (otpInputRefs.current[idx] = el)}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpDigitChange(idx, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                        className={`auth-otp-box ${digit ? "filled" : ""}`}
                        autoFocus={idx === 0}
                        required
                      />
                    ))}
                  </div>

                  {/* Resend & Change Email Row */}
                  <div className="auth-resend-row">
                    {resendCooldown > 0 ? (
                      <span className="auth-resend-timer">
                        Resend in <strong>{resendCooldown}s</strong>
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="auth-resend-btn"
                        onClick={handleResendOtp}
                        disabled={loading}
                      >
                        Resend OTP
                      </button>
                    )}

                    <button
                      type="button"
                      className="auth-back-link"
                      onClick={() => switchTab("signin")}
                    >
                      Change Email
                    </button>
                  </div>

                  {/* Submit Button */}
                  <div className="auth-verify-actions">
                    <motion.button
                      type="submit"
                      disabled={loading || verificationOtp.join("").length !== 6}
                      className="auth-btn-pill"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {loading ? "VERIFYING..." : "VERIFY CODE"}
                    </motion.button>
                  </div>
                </motion.form>
              )}

              {/* ── FORGOT PASSWORD REQUEST FORM ── */}
              {tab === "forgot" && (
                <motion.form
                  key="forgot-form"
                  onSubmit={handleForgotPasswordRequest}
                  variants={formContainerVariants}
                  initial="hidden"
                  animate="show"
                  exit="exit"
                >
                  <div className="auth-verify-intro">
                    <p className="auth-verify-desc">
                      Enter your registered email address to receive a 6-digit password reset code.
                    </p>
                  </div>

                  <div className="auth-form-grid">
                    <motion.div className="auth-line-field" variants={formItemVariants}>
                      <Mail className="auth-line-icon" size={18} />
                      <input
                        name="forgotEmail"
                        type="email"
                        placeholder="Registered Email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        required
                        autoComplete="email"
                        className="auth-line-input"
                        autoFocus
                      />
                    </motion.div>
                  </div>

                  <div className="auth-resend-row" style={{ marginTop: "16px", marginBottom: "8px" }}>
                    <button
                      type="button"
                      className="auth-back-link"
                      onClick={() => switchTab("signin")}
                    >
                      ← Back to Login
                    </button>
                  </div>

                  <div className="auth-verify-actions" style={{ marginTop: "16px" }}>
                    <motion.button
                      type="submit"
                      disabled={loading || !forgotEmail.trim()}
                      className="auth-btn-pill"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {loading ? "SENDING CODE..." : "SEND RESET CODE"}
                    </motion.button>
                  </div>
                </motion.form>
              )}

              {/* ── RESET PASSWORD CONFIRMATION FORM ── */}
              {tab === "reset" && (
                <motion.form
                  key="reset-form"
                  onSubmit={handleResetPasswordSubmit}
                  variants={formContainerVariants}
                  initial="hidden"
                  animate="show"
                  exit="exit"
                >
                  <div className="auth-verify-intro">
                    <p className="auth-verify-desc">
                      Enter the 6-digit code sent to
                    </p>
                    <div className="auth-verify-email-badge">
                      <Mail size={14} />
                      <span>{forgotEmail}</span>
                    </div>
                  </div>

                  {resetDevOtpHint && (
                    <div className="auth-dev-badge">
                      Dev Mode Reset Code: <strong>{resetDevOtpHint}</strong>
                    </div>
                  )}

                  {/* 6-box OTP Input */}
                  <div className="auth-otp-row" onPaste={handleResetOtpPaste}>
                    {resetOtp.map((digit, idx) => (
                      <input
                        key={idx}
                        ref={(el) => (resetOtpInputRefs.current[idx] = el)}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleResetOtpDigitChange(idx, e.target.value)}
                        onKeyDown={(e) => handleResetOtpKeyDown(idx, e)}
                        className={`auth-otp-box ${digit ? "filled" : ""}`}
                        autoFocus={idx === 0}
                        required
                      />
                    ))}
                  </div>

                  {/* Resend & Change Email Row */}
                  <div className="auth-resend-row">
                    {resetCooldown > 0 ? (
                      <span className="auth-resend-timer">
                        Resend in <strong>{resetCooldown}s</strong>
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="auth-resend-btn"
                        onClick={handleResendResetOtp}
                        disabled={loading}
                      >
                        Resend Code
                      </button>
                    )}

                    <button
                      type="button"
                      className="auth-back-link"
                      onClick={() => switchTab("forgot")}
                    >
                      Change Email
                    </button>
                  </div>

                  {/* Password inputs */}
                  <div className="auth-form-grid" style={{ marginTop: "12px" }}>
                    <motion.div className="auth-line-field" variants={formItemVariants}>
                      <Lock className="auth-line-icon" size={18} />
                      <input
                        type={showNewPassword ? "text" : "password"}
                        placeholder="New Password (min 8 chars, 1 uppercase, 1 special)"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        autoComplete="new-password"
                        className="auth-line-input"
                      />
                      <button
                        type="button"
                        className="auth-password-toggle"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        tabIndex={-1}
                      >
                        {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </motion.div>

                    <motion.div className="auth-line-field" variants={formItemVariants}>
                      <Lock className="auth-line-icon" size={18} />
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm New Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        autoComplete="new-password"
                        className="auth-line-input"
                      />
                      <button
                        type="button"
                        className="auth-password-toggle"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </motion.div>
                  </div>

                  {/* Actions */}
                  <div className="auth-verify-actions" style={{ marginTop: "18px" }}>
                    <motion.button
                      type="submit"
                      disabled={loading || resetOtp.join("").length !== 6 || !newPassword || !confirmPassword}
                      className="auth-btn-pill"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {loading ? "RESETTING..." : "RESET PASSWORD"}
                    </motion.button>

                    <button
                      type="button"
                      className="auth-back-link"
                      onClick={() => switchTab("signin")}
                      style={{ marginTop: "6px" }}
                    >
                      Cancel & Back to Login
                    </button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>

          {/* ── Footer Bar (Or Login With + Google) ── */}
          {tab !== "verify" && tab !== "forgot" && tab !== "reset" && (
            <div className="auth-footer-bar">
              <span className="auth-social-label">Or Login With</span>

              <div className="auth-social-buttons">
                <button
                  type="button"
                  className="auth-social-item"
                  onClick={() => handleGoogleLogin()}
                  disabled={loading}
                >
                  <GoogleIcon />
                  <span>Google</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Department Selector Modal */}
      {showDeptSelector && (
        <DeptSelector
          userName={postAuthUser?.name || signUpForm.name || "User"}
          onDone={handleDeptDone}
          onClose={handleDeptClose}
          preSelected={postAuthUser?.departments?.map((d) => d._id) || []}
        />
      )}
    </div>
  );
}
